/**
 * Team & roles (Settings). Owners and admins invite, change roles and remove
 * members; only an owner can grant `owner` (the server answers
 * ROLE_FORBIDDEN otherwise), and the last owner cannot be demoted or removed
 * (409 LAST_OWNER). Removing someone revokes their sessions immediately.
 *
 * Invitations an AI agent asked for (migration 083) are not memberships until
 * the invitee accepts the emailed link; the open ones are listed under
 * "Invitations waiting", where an owner or admin can revoke one. The list
 * stays out of the way when there is nothing to show (or it fails to load).
 */
import { useId, useState, type FormEvent } from 'react';
import { ROLE_DESCRIPTIONS, ROLE_LABELS } from '../auth/roles';
import { useSession } from '../auth/session';
import { PARTNER_ROLES, type Member, type PartnerRole } from '../api/types';
import { Empty, ErrorNotice, Field, Loading, PageHead, Time, useToast } from '../ui/kit';
import { useMutation, usePhone, useQuery } from '../ui/hooks';

export const LAST_OWNER_HINT = 'Add another owner first';

export function Team() {
  const { api, role: myRole, whoami } = useSession();
  const toast = useToast();
  const members = useQuery(() => api.listMembers(), []);
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [role, setRole] = useState<PartnerRole>('developer');
  const invite = useMutation(() => api.inviteMember(email.trim(), role));
  const change = useMutation((userId: number, next: PartnerRole) => api.updateMember(userId, next));
  const remove = useMutation(async (userId: number) => {
    await api.removeMember(userId);
    return true;
  });
  const grantable = PARTNER_ROLES.filter((r) => r !== 'owner' || myRole === 'owner');
  const invites = useQuery(() => api.listMemberInvites(), []);
  const openInvites = (invites.data?.items ?? []).filter((i) => i.status === 'open');
  const revokeInvite = useMutation(async (id: string) => {
    await api.revokeMemberInvite(id);
    return true;
  });

  const phone = usePhone();
  const hintBase = useId();
  const owners = (members.data?.items ?? []).filter((m) => m.role === 'owner').length;
  // The server refuses to demote or remove the only owner (409 LAST_OWNER);
  // the UI says so before anyone tries.
  const lastOwner = (m: Member) => m.role === 'owner' && owners <= 1;
  const hintId = (m: Member) => `${hintBase}-last-${m.user_id}`;
  const hint = (m: Member) => (
    <div className="small muted" id={hintId(m)} style={{ marginTop: 4 }}>
      {LAST_OWNER_HINT}
    </div>
  );
  const roleSelect = (m: Member) => (
    <select
      className="input"
      style={{ minHeight: 32, padding: '3px 8px', width: 'auto', maxWidth: '100%' }}
      aria-label={`Role for ${m.email}`}
      aria-describedby={lastOwner(m) ? hintId(m) : undefined}
      value={m.role}
      disabled={change.pending || lastOwner(m) || (m.role === 'owner' && myRole !== 'owner')}
      onChange={async (e) => {
        if (await change.run(m.user_id, e.target.value as PartnerRole)) {
          toast(`${m.email} is now ${ROLE_LABELS[e.target.value as PartnerRole]}`);
          void members.reload();
        }
      }}
    >
      {PARTNER_ROLES.filter((r) => grantable.includes(r) || r === m.role).map((r) => (
        <option key={r} value={r}>
          {ROLE_LABELS[r]}
        </option>
      ))}
    </select>
  );
  const removeButton = (m: Member) => {
    const me = m.email === whoami?.email;
    return (
      <button
        type="button"
        className="btn btn-sm btn-danger"
        aria-label={`Remove ${m.email}`}
        aria-describedby={lastOwner(m) ? hintId(m) : undefined}
        title={lastOwner(m) ? LAST_OWNER_HINT : me ? 'You can’t remove yourself' : undefined}
        disabled={remove.pending || me || lastOwner(m)}
        onClick={async () => {
          if (!window.confirm(`Remove ${m.email}? Their sessions end immediately.`)) return;
          if (await remove.run(m.user_id)) void members.reload();
        }}
      >
        Remove
      </button>
    );
  };

  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  // Said once they leave the field (or try to send), not while they type.
  const emailIssue =
    emailTouched && email.trim() && !emailOk ? [{ path: 'email', message: 'Enter a full email address, like name@company.com.' }] : [];
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setEmailTouched(true);
    if (!emailOk) return;
    if (await invite.run()) {
      setEmailTouched(false);
      toast(`Invited ${email.trim()} as ${ROLE_LABELS[role]}`);
      setEmail('');
      void members.reload();
    }
  };

  return (
    <div>
      <PageHead title="Team & roles" lede="Invitees sign in at this portal with their email; there is nothing to accept." />
      <div className="stack-lg">
        <section className="panel">
          {members.loading ? (
            <Loading />
          ) : members.error ? (
            <div className="panel-body">
              <ErrorNotice error={members.error} title="Could not load the team" />
            </div>
          ) : members.data?.items.length ? (
            phone ? (
              <div className="cards">
                {members.data.items.map((m) => (
                  <div className="card" key={m.user_id}>
                    <MemberName m={m} me={m.email === whoami?.email} />
                    <span className="small muted">Last sign-in {m.last_login_at ? <Time iso={m.last_login_at} /> : 'not yet'}</span>
                    <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
                      {roleSelect(m)}
                      {removeButton(m)}
                    </div>
                    {lastOwner(m) ? hint(m) : null}
                  </div>
                ))}
              </div>
            ) : (
            <div className="table-wrap">
              <table className="data">
                <caption className="visually-hidden">Members</caption>
                <thead>
                  <tr>
                    <th scope="col">Member</th>
                    <th scope="col">Role</th>
                    <th scope="col">Last sign-in</th>
                    <th scope="col">
                      <span className="visually-hidden">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {members.data.items.map((m) => (
                    <tr key={m.user_id}>
                      <td>
                        <MemberName m={m} me={m.email === whoami?.email} />
                      </td>
                      <td>
                        {roleSelect(m)}
                        {lastOwner(m) ? hint(m) : null}
                      </td>
                      <td>{m.last_login_at ? <Time iso={m.last_login_at} /> : <span className="dotstat warn">Not yet</span>}</td>
                      <td style={{ textAlign: 'right' }}>{removeButton(m)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )
          ) : (
            <Empty title="No members" />
          )}
          {change.error ?? remove.error ? (
            <div className="panel-body">
              <ErrorNotice error={change.error ?? remove.error} />
            </div>
          ) : null}
        </section>
        {openInvites.length ? (
          <section className="panel" aria-labelledby={`${hintBase}-invites`}>
            <div className="panel-head">
              <h2 id={`${hintBase}-invites`}>Invitations waiting</h2>
            </div>
            <div className="panel-body stack-sm">
              <p className="small muted">
                An AI agent asked for these and nobody rejected them in time. Each person joins only if they accept the emailed link
                before it expires.
              </p>
              <ul className="stack-sm" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {openInvites.map((i) => (
                  <li key={i.id} className="row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
                    <span className="wrap-anywhere">
                      <strong>{i.email}</strong> as {ROLE_LABELS[i.role]}
                      {i.agent_label ? <span className="small muted"> · by the AI agent “{i.agent_label}”</span> : null}
                      <span className="small muted">
                        {' '}
                        · expires <Time iso={i.expires_at} />
                      </span>
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      aria-label={`Revoke the invitation for ${i.email}`}
                      disabled={revokeInvite.pending}
                      onClick={async () => {
                        if (await revokeInvite.run(i.id)) {
                          toast(`Revoked the invitation for ${i.email}`);
                          void invites.reload();
                        }
                      }}
                    >
                      Revoke
                    </button>
                  </li>
                ))}
              </ul>
              <ErrorNotice error={revokeInvite.error} />
            </div>
          </section>
        ) : null}
        <section className="panel">
          <div className="panel-head">
            <h2>Invite someone</h2>
          </div>
          <form className="panel-body stack" onSubmit={submit} noValidate>
            <div className="grid-2">
              <Field label="Email" issues={emailIssue}>
                {(p) => (
                  <input
                    {...p}
                    className="input"
                    type="email"
                    autoComplete="off"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => setEmailTouched(true)}
                  />
                )}
              </Field>
              <Field label="Role" hint={ROLE_DESCRIPTIONS[role]}>
                {(p) => (
                  <select {...p} className="input" value={role} onChange={(e) => setRole(e.target.value as PartnerRole)}>
                    {grantable.map((r) => (
                      <option key={r} value={r}>
                        {ROLE_LABELS[r]}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            </div>
            <ErrorNotice error={invite.error} />
            <div>
              <button className="btn btn-primary" type="submit" disabled={invite.pending || !email.trim()}>
                {invite.pending ? 'Inviting…' : 'Send invite'}
              </button>
            </div>
          </form>
        </section>
        <section className="panel panel-pad">
          <dl className="facts">
            {PARTNER_ROLES.map((r) => (
              <div key={r} style={{ display: 'contents' }}>
                <dt>{ROLE_LABELS[r]}</dt>
                <dd>{ROLE_DESCRIPTIONS[r]}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>
    </div>
  );
}

function MemberName({ m, me }: { m: Member; me: boolean }) {
  return (
    <div style={{ minWidth: 0 }}>
      <strong className="wrap-anywhere" style={{ color: 'var(--ink)' }}>{m.name ?? m.email}</strong>
      {me ? <span className="tag" style={{ marginLeft: 6 }}>you</span> : null}
      {m.name ? <div className="small faint wrap-anywhere">{m.email}</div> : null}
    </div>
  );
}
